import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTitle } from '../../utils/pageTitle';
import Iconify from '../../components/iconify';
import EmptyContent from '../../components/empty-content';
import RetryButton from '../../components/buttons/RetryButton';
import { SkeletonCourseCard } from '../../components/skeleton';
import { useStudentCourseBreadcrumb } from '../../layouts/dashboard/student/StudentBreadcrumbs';
import { STUDENT_COURSES_PATH } from '../../layouts/dashboard/student/studentBreadcrumbsLinks';
import { getCourseById } from '../../services/members/courses';

// ----------------------------------------------------------------------

const SKELETON_ITEMS = 4;

export default function WatchCourse() {
  const { course_id } = useParams();
  const navigate = useNavigate();

  const {
    data: course,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryFn: () => getCourseById({ courseId: course_id }),
    queryKey: ['members-courses', course_id],
    select(data) {
      return {
        name: data.name,
        videos: data.modules.map((module) => ({
          id: module.id,
          title: module.name,
          duration: '',
          videoUrl: module.coverImage.preview,
        })),
      };
    },
  });

  useStudentCourseBreadcrumb(course?.name);

  const goToCourses = () => navigate(STUDENT_COURSES_PATH);

  return (
    <Container maxWidth="xl" sx={{ pb: 5 }}>
      <PageTitle area="Aluno" screen={course?.name || 'Curso'} />

      {isLoading ? (
        <Skeleton variant="text" sx={{ width: { xs: 1, sm: 320 }, height: 48, mb: 3 }} />
      ) : (
        <Typography variant="h4" sx={{ mb: 3 }}>
          {course?.name}
        </Typography>
      )}

      {isError && (
        <RetryButton message="Não foi possível carregar as aulas deste curso." onClick={refetch} />
      )}

      {!isError && (
        <Grid container spacing={2}>
          {isLoading &&
            [...Array(SKELETON_ITEMS)].map((_, index) => (
              <Grid data-testid="loading" key={index} item xs={12} md={6} lg={4}>
                <SkeletonCourseCard />
              </Grid>
            ))}

          {!isLoading &&
            course?.videos?.map((item) => (
              <Grid item key={item.id} xs={12} md={6} lg={4}>
                <Card
                  sx={{
                    height: 1,
                    width: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardMedia
                    component="img"
                    height="240"
                    image={item.videoUrl}
                    alt={item.title}
                    sx={{ objectFit: 'cover' }}
                  />

                  <CardContent sx={{ mt: 'auto', width: 1 }}>
                    <Typography variant="h6" component="div">
                      {item.title}
                    </Typography>

                    {item.duration && (
                      <Typography variant="body2" color="text.secondary">
                        {`Duração: ${item.duration}`}
                      </Typography>
                    )}

                    <Button
                      endIcon={<Iconify icon="mdi:play" />}
                      variant="contained"
                      sx={{ mt: 1, width: 1 }}
                    >
                      Reproduzir
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}

          {!isLoading && !course?.videos?.length && (
            <Grid item xs={12}>
              <EmptyContent
                title="Nenhuma aula disponível"
                description="Este curso ainda não possui aulas publicadas. Assim que o produtor liberar o conteúdo, ele aparecerá aqui."
                action={
                  <Button variant="contained" onClick={goToCourses}>
                    Voltar aos cursos
                  </Button>
                }
              />
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
}
